import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { usePlan } from './PlanContext';
import { BACKEND_URL } from '../config';

const OrganizationContext = createContext();

// ═══════════════════════════════════════════════════════════════════════════════
// GLOBAL COMPLETION FLAG - Set before navigation, survives across renders
// This prevents org switching reload during assessment completion flow
// ═══════════════════════════════════════════════════════════════════════════════
const COMPLETION_IN_PROGRESS_KEY = 'mm_assessment_completion_in_progress';
const ACTIVE_ORG_KEY = 'mm_active_org_id';

// Set this flag BEFORE navigation to results page
export const setCompletionInProgress = (assessmentId) => {
  const data = {
    assessmentId,
    timestamp: Date.now(),
    expiresAt: Date.now() + 5 * 60 * 1000 // 5 minutes
  };
  sessionStorage.setItem(COMPLETION_IN_PROGRESS_KEY, JSON.stringify(data));
  console.log('[COMPLETION_FLAG] Set completion in progress:', assessmentId);
};

// Clear the flag (call on explicit user action)
export const clearCompletionInProgress = () => {
  sessionStorage.removeItem(COMPLETION_IN_PROGRESS_KEY);
  console.log('[COMPLETION_FLAG] Cleared completion in progress');
};

// Check if completion is in progress
const isCompletionInProgress = () => {
  try {
    const stored = sessionStorage.getItem(COMPLETION_IN_PROGRESS_KEY);
    if (!stored) return false;
    
    const data = JSON.parse(stored);
    if (Date.now() > data.expiresAt) {
      sessionStorage.removeItem(COMPLETION_IN_PROGRESS_KEY);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// PHASE 3.2: Organization Context with Explicit Org Resolution
// ═══════════════════════════════════════════════════════════════════════════════

export const OrganizationProvider = ({ children }) => {
  const { userPlan, isAuthenticated } = usePlan();
  const [organizations, setOrganizations] = useState([]);
  const [activeOrganization, setActiveOrganizationState] = useState(null);
  const [activeOrgContext, setActiveOrgContext] = useState(null); // Phase 3.1 context
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState(null);
  
  // Callback refs for state clearing on org switch
  const switchCallbacksRef = useRef([]);

  // Register a callback to be called when org switches
  const onOrgSwitch = useCallback((callback) => {
    switchCallbacksRef.current.push(callback);
    return () => {
      switchCallbacksRef.current = switchCallbacksRef.current.filter(cb => cb !== callback);
    };
  }, []);

  // Fetch organizations using Phase 3.1 API
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      
      // Use the new Phase 3.1 endpoint for accessible orgs
      const response = await fetch(`${BACKEND_URL}/api/org/accessible`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const orgs = data.organizations || [];
        setOrganizations(orgs);
        
        // Set active org from cache or first org
        if (orgs.length > 0) {
          const cachedOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
          const activeOrg = orgs.find(o => o.organization_id === cachedOrgId) || orgs[0];
          setActiveOrganizationState(activeOrg);
          
          if (activeOrg) {
            localStorage.setItem(ACTIVE_ORG_KEY, activeOrg.organization_id);
            // Fetch full org context
            await fetchOrgContext(activeOrg.organization_id);
          }
        }
      } else {
        // Fallback to legacy endpoint
        const legacyResponse = await fetch(`${BACKEND_URL}/api/organizations`, {
          credentials: 'include'
        });
        
        if (legacyResponse.ok) {
          const orgs = await legacyResponse.json();
          setOrganizations(orgs);
          
          if (orgs.length > 0) {
            const cachedOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
            const activeOrg = orgs.find(o => o.organization_id === cachedOrgId) || orgs[0];
            setActiveOrganizationState(activeOrg);
            
            if (activeOrg) {
              localStorage.setItem(ACTIVE_ORG_KEY, activeOrg.organization_id);
            }
          }
        } else {
          setError('Failed to fetch organizations');
        }
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError('Error loading organizations');
    } finally {
      setLoading(false);
    }
  };

  // Fetch organization context (Phase 3.1)
  const fetchOrgContext = async (orgId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/org/context`, {
        credentials: 'include',
        headers: orgId ? { 'X-Organization-Id': orgId } : {}
      });
      
      if (response.ok) {
        const context = await response.json();
        setActiveOrgContext(context);
        return context;
      }
    } catch (error) {
      console.error('Error fetching org context:', error);
    }
    return null;
  };

  useEffect(() => {
    if (isAuthenticated && userPlan) {
      fetchOrganizations();
    }
  }, [isAuthenticated, userPlan]);

  // Switch organization using Phase 3.1 API
  const setActiveOrganization = async (orgId) => {
    console.log('[ORG_SWITCH] setActiveOrganization called with orgId:', orgId);
    
    const org = organizations.find(o => o.organization_id === orgId);
    if (!org) {
      console.warn('[ORG_SWITCH] Organization not found:', orgId);
      return;
    }
    
    // Skip if same org
    if (activeOrganization?.organization_id === orgId) {
      console.log('[ORG_SWITCH] Already active, skipping');
      return;
    }
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // COMPLETION GUARD: Don't switch if user is in assessment completion flow
    // ═══════════════════════════════════════════════════════════════════════════════
    
    if (window.__MM_COMPLETION_IN_PROGRESS) {
      console.log('[BLOCKED_SWITCH] reason=window_completion_flag');
      return;
    }
    
    if (isCompletionInProgress()) {
      console.log('[BLOCKED_SWITCH] reason=completion_flag_active');
      return;
    }
    
    const urlHasSuccess = window.location.search.includes('success=1');
    if (urlHasSuccess) {
      console.log('[BLOCKED_SWITCH] reason=success_param_in_url');
      return;
    }
    
    setSwitching(true);
    
    try {
      // Call Phase 3.1 switch endpoint to validate and get context
      const response = await fetch(`${BACKEND_URL}/api/org/switch`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: orgId })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('[ORG_SWITCH] Switch successful:', result);
        
        // Update state
        setActiveOrganizationState(org);
        setActiveOrgContext({
          org_id: result.org_id,
          org_name: result.org_name,
          role: result.role,
          is_owner: result.is_owner,
          is_admin: result.is_admin,
          parent_org_id: result.parent_org_id,
          source: 'switch'
        });
        localStorage.setItem(ACTIVE_ORG_KEY, orgId);
        
        // Call registered callbacks to clear cached data
        switchCallbacksRef.current.forEach(cb => {
          try {
            cb(orgId, result);
          } catch (e) {
            console.error('[ORG_SWITCH] Callback error:', e);
          }
        });
        
        // Set flag and reload to ensure clean state
        sessionStorage.setItem('mm_org_switching', 'true');
        window.location.reload();
        
      } else {
        const errorData = await response.json();
        console.error('[ORG_SWITCH] Switch failed:', errorData);
        setError(errorData.detail || 'Failed to switch organization');
      }
    } catch (error) {
      console.error('[ORG_SWITCH] Error:', error);
      setError('Network error switching organization');
    } finally {
      setSwitching(false);
    }
  };

  const createOrganization = async (name) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/organizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });
      
      if (response.ok) {
        await fetchOrganizations();
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Failed to create organization' };
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const updateOrganization = async (orgId, name) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/organizations/${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });
      
      if (response.ok) {
        await fetchOrganizations();
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const deleteOrganization = async (orgId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/organizations/${orgId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchOrganizations();
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Get the current org ID for API calls (for X-Organization-Id header)
  const getActiveOrgId = useCallback(() => {
    return activeOrganization?.organization_id || localStorage.getItem(ACTIVE_ORG_KEY);
  }, [activeOrganization]);

  // Check if user can manage child orgs (Phase 3.3)
  const canManageChildOrgs = useCallback(() => {
    if (!activeOrgContext) return false;
    return activeOrgContext.is_admin && !activeOrgContext.parent_org_id;
  }, [activeOrgContext]);

  const value = {
    // State
    organizations,
    activeOrganization,
    activeOrgContext, // Phase 3.1 context
    loading,
    switching,
    error,
    
    // Actions
    setActiveOrganization,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    refreshOrganizations: fetchOrganizations,
    
    // Phase 3.2 helpers
    getActiveOrgId,
    onOrgSwitch,
    hasMultipleOrgs: organizations.length > 1,
    
    // Phase 3.3 helpers
    canManageChildOrgs,
    
    // Plan check (handle both formats)
    isPartnerPro: userPlan === 'partner-pro' || userPlan === 'partner_pro'
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

// Hook to get org header for API calls
export const useOrgHeader = () => {
  const { getActiveOrgId } = useOrganization();
  
  return useCallback(() => {
    const orgId = getActiveOrgId();
    return orgId ? { 'X-Organization-Id': orgId } : {};
  }, [getActiveOrgId]);
};
